import { useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Button } from '@artosera/ui';
import { reserveApi, useMutationReserve, type DeplacementInventaire } from '../../api/reserve.js';
import { dansLaPorteeCourante } from '../../api/cles.js';
import { estApiError } from '../../api/errors.js';
import { galerieALaction } from '../../auth/sujet.js';
import { useShellContext } from '../shell/context.js';
import { Scanner, type DecoderQr, type DecoderQrMultiple } from './Scanner.js';
import { ScanSelection } from './ScanSelection.js';
import { CampagnesPage } from './CampagnesPage.js';
import { CaissesPage } from './CaissesPage.js';
import styles from './InventairePage.module.css';

type LigneRangement = {
  cible: DeplacementInventaire['cibles'][number];
  titre: string;
  origine: string;
};
export function InventairePage({
  decoderQr,
  decoderQrMultiple,
}: {
  decoderQr?: DecoderQr;
  decoderQrMultiple?: DecoderQrMultiple;
}) {
  const { t } = useTranslation(['regie', 'commun']);
  const { galerieSlug, galerie } = useShellContext();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const onglet = params.get('onglet') ?? 'consulter';
  const [destination, setDestination] = useState<{
    corps: DeplacementInventaire['destination'];
    nom: string;
  } | null>(null);
  const [lignes, setLignes] = useState<LigneRangement[]>([]);
  const [erreur, setErreur] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const cle = useRef<string | null>(null);
  const mutation = useMutationReserve(galerieSlug, (corps: DeplacementInventaire, appel) =>
    reserveApi.deplacer(galerieSlug, corps, appel),
  );
  const peutEcrire = galerieALaction(galerie, 'inventaire:write');
  async function scanner(code: string) {
    setErreur('');
    setConfirmation('');
    try {
      const resolution = await dansLaPorteeCourante((appel) =>
        reserveApi.resoudre(galerieSlug, code, appel),
      );
      const { cible, resume } = resolution;
      if (onglet === 'consulter') {
        if (cible.type === 'emplacement')
          navigate(`/${galerieSlug}/reglages/emplacements/${cible.id}`);
        else if (cible.type === 'caisse')
          navigate(`/${galerieSlug}/inventaire/caisses/${cible.id}`);
        else if ('oeuvreId' in resume) navigate(`/${galerieSlug}/oeuvres/${resume.oeuvreId}`);
        return;
      }
      if (cible.type === 'emplacement') {
        const { emplacement } = await dansLaPorteeCourante((appel) =>
          reserveApi.emplacement(galerieSlug, cible.id, appel),
        );
        setDestination({
          corps: { lieuId: emplacement.lieuId, emplacementId: emplacement.id },
          nom: emplacement.chemin,
        });
        cle.current = null;
      } else if (cible.type === 'caisse') {
        setDestination({
          corps: { caisseId: cible.id },
          nom: 'numero' in resume ? resume.numero : '',
        });
        cle.current = null;
      } else {
        if (!destination) {
          setErreur(t('regie:inventaire.destinationDabord'));
          return;
        }
        if (lignes.some((l) => l.cible.id === cible.id && l.cible.type === cible.type)) return;
        if (!('titre' in resume)) return;
        const origine = resume.lieuActuel || t('regie:inventaire.sansLieu');
        const cibleOeuvre: LigneRangement['cible'] = { type: cible.type, id: cible.id };
        setLignes((avant) => [
          ...avant,
          {
            cible: cibleOeuvre,
            titre: `${resume.titre} ${resume.numeroExemplaire ?? ''}`.trim(),
            origine,
          },
        ]);
        cle.current = null;
      }
    } catch (e) {
      setErreur(estApiError(e) ? e.message : t('commun:erreurReseau'));
    }
  }
  async function confirmer() {
    if (!destination || !lignes.length) return;
    if (!navigator.onLine) {
      setErreur(t('regie:inventaire.reseauRequis'));
      return;
    }
    const cleIdempotence = cle.current ?? crypto.randomUUID();
    cle.current = cleIdempotence;
    setErreur('');
    try {
      await mutation.mutateAsync({
        cleIdempotence,
        destination: destination.corps,
        cibles: lignes.map((l) => l.cible),
      });
      setLignes([]);
      cle.current = null;
      setConfirmation(t('regie:inventaire.range'));
    } catch (e) {
      setErreur(estApiError(e) ? e.message : t('commun:erreurReseau'));
    }
  }
  return (
    <section className={styles.page} data-ui="page">
      <header className={styles.entete} data-ui="page-heading">
        <div>
          <h1>{t('regie:inventaire.titre')}</h1>
          <p>{t('regie:inventaire.sousTitre')}</p>
        </div>
      </header>
      <nav className={styles.onglets} aria-label={t('regie:inventaire.onglets')}>
        {['consulter', 'ranger', 'selection', 'campagnes', 'caisses']
          .filter((o) => o !== 'ranger' || peutEcrire)
          .map((o) => (
            <Link
              key={o}
              to={`?onglet=${o}`}
              aria-current={onglet === o ? 'page' : undefined}
              onClick={(e) => {
                if (mutation.isPending) {
                  e.preventDefault();
                  return;
                }
                setParams({ onglet: o });
                setErreur('');
              }}
            >
              {t(`regie:inventaire.${o}`)}
            </Link>
          ))}
      </nav>
      {onglet === 'campagnes' && <CampagnesPage />}
      {onglet === 'caisses' && <CaissesPage />}
      {onglet === 'selection' && <ScanSelection decoderQrMultiple={decoderQrMultiple} />}
      {(onglet === 'consulter' || (onglet === 'ranger' && peutEcrire)) && (
        <>
          {onglet === 'ranger' && <p>{t('regie:inventaire.rangerInstructions')}</p>}
          <Scanner decoderQr={decoderQr} onScan={scanner} desactive={mutation.isPending} />
          {onglet === 'ranger' && destination && (
            <section className={styles.panneau} aria-label={t('regie:inventaire.recapitulatif')}>
              <h2>
                {t('regie:inventaire.destination')} : {destination.nom}
              </h2>
              <ul className={styles.arbre}>
                {lignes.map((l) => (
                  <li key={`${l.cible.type}:${l.cible.id}`} className={styles.ligne}>
                    <div>
                      <strong>{l.titre}</strong>
                      <p>
                        {l.origine} → {destination.nom}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      disabled={mutation.isPending}
                      onClick={() => {
                        setLignes(lignes.filter((ligne) => ligne !== l));
                        cle.current = null;
                      }}
                    >
                      {t('regie:inventaire.retirer')}
                    </Button>
                  </li>
                ))}
              </ul>
              <Button
                disabled={!lignes.length || mutation.isPending}
                loading={mutation.isPending}
                onClick={() => void confirmer()}
              >
                {t('regie:inventaire.confirmer')}
              </Button>
            </section>
          )}
        </>
      )}
      {erreur && (
        <p role="alert" className={styles.erreur}>
          {erreur}
        </p>
      )}
      {confirmation && <p role="status">{confirmation}</p>}
    </section>
  );
}
